/**
 * BlogCard Component
 * Displays a blog post preview card with hover animations
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { BLOG_CATEGORIES } from '../../content/blogs';

const BlogCard = ({ blog, index = 0 }) => {
  const { i18n, t } = useTranslation();
  const language = i18n.language || 'es';

  const category = BLOG_CATEGORIES[blog.category];
  const title = blog.title[language] || blog.title.es;
  const excerpt = blog.excerpt[language] || blog.excerpt.es;
  const categoryLabel = category?.label[language] || category?.label.es;

  // Category color mapping using design system colors
  const categoryColors = {
    primary:
      'bg-primary-500/10 text-primary-600 dark:bg-primary-500/20 dark:text-primary-400',
    secondary:
      'bg-secondary-500/10 text-secondary-600 dark:bg-secondary-500/20 dark:text-secondary-400',
    accent:
      'bg-accent-500/10 text-accent-600 dark:bg-accent-500/20 dark:text-accent-400',
    info: 'bg-info-500/10 text-info-600 dark:bg-info-500/20 dark:text-info-400',
    success:
      'bg-success-500/10 text-success-600 dark:bg-success-500/20 dark:text-success-400',
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      whileHover={{ scale: 1.02 }}
      className="group"
    >
      <Link
        to={`/blog/${blog.slug}`}
        className={cn(
          'block h-full rounded-2xl p-6',
          'border border-neutral-200 bg-white',
          'dark:border-neutral-800 dark:bg-neutral-900',
          'transition-[background-color,border-color,color,box-shadow,opacity,transform,width,height] duration-300',
          'hover:border-primary-300 hover:shadow-lg hover:shadow-primary-500/5',
          'dark:hover:border-primary-700 dark:hover:shadow-primary-500/10'
        )}
      >
        {/* Category Badge */}
        <div className="mb-4 flex items-center justify-between">
          <span
            className={cn(
              'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium',
              categoryColors[category?.color] || categoryColors.primary
            )}
          >
            {categoryLabel}
          </span>
          <span className="flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400">
            <Clock className="h-3 w-3" strokeWidth={2.5} />
            {blog.readingTime} {t('blog.minRead', 'min')}
          </span>
        </div>

        {/* Title */}
        <h3 className="mb-3 text-lg font-semibold text-neutral-900 transition-colors group-hover:text-primary-600 dark:text-white dark:group-hover:text-primary-400">
          {title}
        </h3>

        {/* Excerpt */}
        <p className="mb-4 line-clamp-3 text-sm text-neutral-600 dark:text-neutral-400">
          {excerpt}
        </p>

        {/* Read More */}
        <div className="flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400">
          <span>{t('blog.readMore', 'Leer más')}</span>
          <ArrowRight
            className="h-4 w-4 transition-transform group-hover:translate-x-1"
            strokeWidth={2.5}
          />
        </div>
      </Link>
    </motion.article>
  );
};

export default BlogCard;
